import { Box, Tab } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcBarTabs, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { MprcSimpleDialog } from 'components/MprcSimpleDialog';
import { SupervisorEnrollmentDetailsResponse } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnrollmentComponents } from 'views/lms/student/EnrollmentView/Shared/EnrollmentComponents';
import { EnrollmentSummary } from 'views/lms/student/EnrollmentView/Shared/EnrollmentSummary';
import { EnrollmentScores } from 'views/lms/student/EnrollmentView/Shared/ScoreDetailDialog';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    enrollmentId: string;
    subtitle: string;
    onClose: () => void;
}

type TabOptions = "summary" | "components" | "scoreComponents";

////////////////////////////////////////////////////////////////////////////

export const EnrollmentDetailsDialog: React.FC<IProps> = ({ enrollmentId, subtitle, onClose }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(true);
    const [currentTab, setCurrentTab] = useState<TabOptions>("summary");
    const [viewData, setViewData] = useState<SupervisorEnrollmentDetailsResponse>();
    const [viewError, setViewError] = useState<any>();

    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.lmsMonitoring.getSupervisorEnrollmentDetails({ enrollmentId })
            .then(({ data }) => setViewData(data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, []);

    ////////////////////////////////////////////////////////////////////////////

    return <MprcSimpleDialog
        title={t("Monitoring.Enrollment.Details.Title")}
        subtitle={subtitle}
        message={
            <Box>
                <MprcRequestIndicator visible={loading} />
                {viewError ?
                    <MprcRequestErrorReport error={viewError} />
                    :
                    !loading &&
                    <>
                        <MprcBarTabs sx={{ marginBottom: 3 }}
                            variant="fullWidth"
                            value={currentTab}
                            onChange={(v, selected) => setCurrentTab(selected)}>
                            <Tab
                                value={"summary"}
                                label={t("Monitoring.Enrollment.Details.SummaryTitle")}
                            />
                            {(viewData?.components?.length ?? 0) > 0 &&
                                <Tab
                                    value={"components"}
                                    label={t("Monitoring.Enrollment.Details.ComponentsTitle")}
                                />
                            }
                            {(viewData?.scoreComponents?.length ?? 0) > 0 &&
                                <Tab
                                    value={"scoreComponents"}
                                    label={t("Monitoring.Enrollment.Details.ScoreTitle")}
                                />
                            }
                        </MprcBarTabs>
                        <Box sx={{ height: "400px", overflow: "auto" }} className={AppGlobalClassKeys.thinScrollbar}>
                            {viewData && currentTab == "summary" && <EnrollmentSummary data={viewData.summary} mode="instructor" />}
                            {viewData && currentTab == "components" && <EnrollmentComponents items={viewData.components} readonly variant="vertical" />}
                            {viewData && currentTab == "scoreComponents" && <EnrollmentScores data={viewData.scoreComponents} />}
                        </Box>
                    </>
                }
            </Box>
        }
        onClose={onClose}

    />;
}
